import { useEffect, useState, useContext, useMemo } from "react";
import {
  Typography,
  Paper
} from "@mui/material";
import ArrayEditor from './ArrayEditor.js'
import Page from "../../layout/Page.js";
import FormViewCommandBar from "../../commandBar/FormViewCommandBar.js";
import { metadataLabelInUse } from "../data/settings.js";
import { getResourceLibraryFileByPath, uploadFileToPath } from '../data/index.js'
import EventBus from "../../utils/EventBus.js";
import EventNames from "../../utils/EventNames.js";
import CacheContext from '../../cache/CacheContext.js'
import ReassignmentDialog from "./ReassignmentDialog.js";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog.js";
import { getSiteConfig } from "../../community/siteProvider.js";
import { useLocation } from "react-router-dom";

export default function CommunityHubAdminPage(props = {}) {
  const [config, setConfig] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reassignmentDialogOpen, setReassignmentDialogOpen] = useState('');
  const [reassignmentDialogProps, setReassignmentDialogProps] = useState({});
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState('');
  const [confirmDeleteDialogProps, setConfirmDeleteDialogProps] = useState({});

  const cacheContext = useContext(CacheContext)

  const { pathname } = useLocation()
    
  const siteKey = useMemo(() => {
    return pathname.split('/').filter(Boolean)[0] ?? null
  }, [pathname])
  
  const loadConfig = async () => {
    const _siteConfig = getSiteConfig()
    setSiteConfig(_siteConfig)

    try {
      const file = await getResourceLibraryFileByPath(_siteConfig.settingsFilePath)
      setConfig(file)
    } catch (error) {
      setConfig({})
    }
    setLoading(false);
  };

  useEffect(() => {
    console.log(siteKey)
    loadConfig();
  }, [siteKey]);

  // --- Save function: replace with Graph API PUT request ---
  const saveConfig = async () => {
    const { settingsFilePath } = siteConfig
    setLoading(true)
    setSaving(true);
    try {
      await uploadFileToPath(settingsFilePath, JSON.stringify(config), 'application/json')
      setDirty(false)
      EventBus.emit(EventNames.ALERT, {
        text: 'Successfully Saved Settings',
        duration: 2000,
        style: 'success'
      })
    } finally {
      setSaving(false);
      setLoading(false)
    }
  };

  // if (loading) return <Typography>Loading...</Typography>;

  async function onClickChip(chipName, section, key) {
    setReassignmentDialogOpen(chipName)
    setReassignmentDialogProps({
      label: chipName,
      entityType: section,
      itemType: key,
      reassignmentOptions: (config?.[section]?.[key] || []).filter(f => f !== chipName)
    })
  }

  async function validateDelete(toDelete, section, key) {
    const inUse = await metadataLabelInUse({
      tag: key === 'tags' ? toDelete : undefined,
      category: key === 'categories' ? toDelete : undefined,
      list: section,
      fetchListItems: true
    }, cacheContext)
    return inUse
  }

  const onCancel = () => {
    setLoading(true)
    setDirty(false)
    loadConfig()
    setLoading(false)
  }

  useEffect(() => {
    (async () => {
      if(dirty) {
        await saveConfig()
      }
    })()
  }, [config, dirty])

  return (
    <Page
      {...props}
      breadcrumb={`${siteConfig?.label} Settings`}
      isLoading={loading}
      commandBar={
      <FormViewCommandBar
        onCancel={onCancel}
        showSave={false}
        showCancel={!saving && dirty}
        showClose={false}
      />
    }
    >
      <ConfirmDeleteDialog
        open={confirmDeleteDialogOpen}
        {...confirmDeleteDialogProps}
        // onClose={}
        // onConfirm
        // itemName
      />

      <ReassignmentDialog
        open={reassignmentDialogOpen}
        onClose={() => {
          setReassignmentDialogOpen()
          setReassignmentDialogProps({})
        }}
        {...reassignmentDialogProps}
      />

        {[
          {
            section: "articles",
            sectionLabel: "Articles"
          },
          {
            section: "resources",
            sectionLabel: "Resource Library"
          }].map(({section, sectionLabel}) => (
          <Paper sx={{padding: '1em', margin: '1em'}} key={sectionLabel}>
            <Typography variant="h5">{sectionLabel}</Typography>

            {/* Numeric fields */}
            {/* {config[section].pinCount !== undefined && (
              <TextField
                label="Pin Count"
                type="number"
                value={config[section].pinCount}
                onChange={(e) => {
                  setDirty(true)
                  setConfig({
                    ...config,
                    [section]: {
                      ...config[section],
                      pinCount: Number(e.target.value),
                    },
                  })
                }}
                helperText="The number pin positions available for pinned articles."
                sx={{ mt: 2, mr: 2 }}
              />
            )} */}
            {/* {config[section].pageSize !== undefined && (
              <TextField
                label="Page Size"
                type="number"
                value={config[section].pageSize}
                onChange={(e) => {
                  setDirty(true)
                  setConfig({ ...config, [section]: { ...config[section], pageSize: Number(e.target.value) } })
                }}
                sx={{ mt: 2 }}
                helperText={<p>The number number of articles that load per category in a fresh page.<br />Additional articles are loaded once you click "Load More"</p>}
              />
            )} */}

            {/* Editable arrays */}
            {[{
              key: "tags",
              helperText: "",
              label: "Tag",
            },
            {
              key: "categories",
              helperText: "",
              label: "Category",
            }]
            .map(({ key, helperText, label }) => (
              <ArrayEditor
                key={key}
                label={label}
                section={section}
                keyName={key}
                values={config?.[section]?.[key]}
                validateDelete={(item) => validateDelete(item, section, key)}
                onClickChip={(item) => onClickChip(item, section, key)}
                onUpdate={(updatedArray, itemName, showConfirm) => {
                  const applyUpdate = () => {
                    setDirty(true)
                    setConfig((prev) => ({
                      ...prev,
                      [section]: {
                        ...prev[section],
                        [key]: updatedArray
                      }
                    }))
                  }
                  if(showConfirm) {
                    setConfirmDeleteDialogProps({
                      onConfirm: () => { applyUpdate(); setConfirmDeleteDialogOpen(false) },
                      itemName,
                      onClose: () => setConfirmDeleteDialogOpen(false)
                    })
                    setConfirmDeleteDialogOpen(true)
                  } else {
                    applyUpdate()
                  }
                }}
              />
            ))}
          </Paper>
        ))}
    </Page>
  );
}